import { inject } from '@angular/core';
import { Account } from '@mkp/account/data-access';
import { selectActiveAccounts } from '@mkp/account/state';
import {
  authActions,
  authOnboardingRecoveryActions,
  OnboardingRecoveryStep,
} from '@mkp/auth/actions';
import { CompanyProfile } from '@mkp/company/models';
import { getOptionalContext } from '@mkp/core/feature-interceptor';
import { SentryService } from '@mkp/core/util-sentry';
import { LegalEntity } from '@mkp/legal-entity/data-access';
import { OnboardingApiService, OnboardingRecoveryService } from '@mkp/onboarding/data-access';
import { getMessageError, walletQueryBuilder, WalletResource } from '@mkp/shared/data-access';
import { UUID, WalletDto } from '@mkp/shared/models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { forkJoin, map, Observable, of } from 'rxjs';
import { catchError, exhaustMap, filter, switchMap } from 'rxjs/operators';

const SHOULD_HANDLE_MEMBERSHIP_RECOVERY = false;

// Login carries at most 200 memberships, each able to own several wallets.
const WALLET_DETECTION_PAGE_LIMIT = 500;

export const onboardOrRecover = createEffect(
  (actions$ = inject(Actions), onboardingApiService = inject(OnboardingApiService)) =>
    actions$.pipe(
      ofType(authActions.noAccountsFoundAfterUserLogin),
      switchMap(() =>
        onboardingApiService.findInitialLegalEntity().pipe(
          switchMap((legalEntityDto) =>
            legalEntityDto
              ? of(
                  authOnboardingRecoveryActions.onboardingFailedFromAccount({
                    legalEntityDto,
                  })
                )
              : of(authActions.loadLegalEntityForUserWithNoAccountFailed())
          ),
          catchError(() => of(authOnboardingRecoveryActions.onboardingRecoveryFailed()))
        )
      )
    ),
  { functional: true }
);

export const resumeOnboardingWhenFailedFromAccount = createEffect(
  (actions$ = inject(Actions), onboardingRecoveryService = inject(OnboardingRecoveryService)) =>
    actions$.pipe(
      ofType(authOnboardingRecoveryActions.onboardingFailedFromAccount),
      switchMap(({ legalEntityDto }) =>
        onboardingRecoveryService.recoverFromAccountFlow(legalEntityDto).pipe(
          switchMap(() =>
            of(authOnboardingRecoveryActions.onboardingRecoverySuccess({ step: 'account' }))
          ),
          catchError(() => of(authOnboardingRecoveryActions.onboardingRecoveryFailed()))
        )
      )
    ),
  { functional: true }
);

export const resumeOnboardingWhenFailedFromMembership = createEffect(
  (actions$ = inject(Actions), onboardingRecoveryService = inject(OnboardingRecoveryService)) =>
    actions$.pipe(
      ofType(authOnboardingRecoveryActions.onboardingFailedFromMembership),
      switchMap(({ account }) =>
        SHOULD_HANDLE_MEMBERSHIP_RECOVERY
          ? onboardingRecoveryService.recoverFromMembershipsFlow(account).pipe(
              switchMap(() =>
                of(authOnboardingRecoveryActions.onboardingRecoverySuccess({ step: 'membership' }))
              ),
              catchError(() => of(authOnboardingRecoveryActions.onboardingRecoveryFailed()))
            )
          : of(authOnboardingRecoveryActions.onboardingRecoveryFailed())
      )
    ),
  { functional: true }
);

export const fetchWalletsAfterLogin = createEffect(
  (actions$ = inject(Actions), walletResource = inject(WalletResource)) =>
    actions$.pipe(
      ofType(authActions.loadActiveAccountMembershipsSuccess),
      map(({ accountMemberships }) => [
        ...new Set(accountMemberships.map(({ accountId }) => accountId)),
      ]),
      switchMap((accountIds) =>
        walletResource
          .queryWithEnvelope(
            walletQueryBuilder()
              .equal('walletAccesses.account.id', accountIds)
              .build({ context: getOptionalContext(), limit: WALLET_DETECTION_PAGE_LIMIT })
          )
          .pipe(
            map(({ _embedded: { results }, totalCount }) =>
              getAccountIdsWithoutWallet(accountIds, results, totalCount)
            ),
            map((accountIdsWithoutWallet) =>
              accountIdsWithoutWallet.length
                ? authActions.noWalletsFoundAfterLogin({ accountIds: accountIdsWithoutWallet })
                : authActions.loadWalletsAfterLoginSuccess()
            ),
            catchError((error) =>
              of(
                authActions.loadWalletsAfterLoginFailed({
                  error: getMessageError(error, 'initiateVerifiedUserAccountMemberships'),
                })
              )
            )
          )
      )
    ),
  { functional: true }
);

export const handleNoWalletsAfterLogin = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(authActions.noWalletsFoundAfterLogin),
      map(({ accountIds }) =>
        authOnboardingRecoveryActions.onboardingFailedFromWallet({ accountIds })
      )
    ),
  { functional: true }
);

export const recoverFromFailedWalletOnboarding = createEffect(
  (
    actions$ = inject(Actions),
    onboardingRecoveryService = inject(OnboardingRecoveryService),
    sentryService = inject(SentryService)
  ) =>
    actions$.pipe(
      ofType(authOnboardingRecoveryActions.onboardingFailedFromWallet),
      filter(({ accountIds }) => accountIds.length > 0),
      // Recovery success restarts login, so a re-trigger must not cancel an in-flight create.
      exhaustMap(({ accountIds }) =>
        recoverEach(
          accountIds,
          (accountId) => onboardingRecoveryService.recoverFromWalletFlow(accountId),
          reportRecoveryFailure(sentryService, 'wallet')
        ).pipe(map(toRecoveryAction('wallet')))
      )
    ),
  { functional: true }
);

export const recoverFromFailedCompanyOnboarding = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    onboardingRecoveryService = inject(OnboardingRecoveryService),
    sentryService = inject(SentryService)
  ) =>
    actions$.pipe(
      ofType(authOnboardingRecoveryActions.onboardingFailedFromCompany),
      concatLatestFrom(() => store.select(selectActiveAccounts)),
      map(([{ accountIds }, accounts]) => getAccountsToRecover(accounts, accountIds)),
      filter((accountsToRecover) => accountsToRecover.length > 0),
      exhaustMap((accountsToRecover) =>
        recoverEach(
          accountsToRecover,
          (account) => onboardingRecoveryService.recoverFromCompanyFlow(account.legalEntity.id),
          reportRecoveryFailure(sentryService, 'companyProfile')
        ).pipe(map(toRecoveryAction('companyProfile')))
      )
    ),
  { functional: true }
);

export const checkLocationAfterLogin = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(authActions.loadCompaniesAfterLoginSuccess),
      map(({ companyProfiles }) => companyProfiles.filter(hasNoLocation)),
      filter((companyProfilesWithoutLocation) => companyProfilesWithoutLocation.length > 0),
      map((companyProfiles) =>
        authOnboardingRecoveryActions.onboardingFailedFromLocation({ companyProfiles })
      )
    ),
  { functional: true }
);

export const recoverFromFailedLocationAfterLogin = createEffect(
  (
    actions$ = inject(Actions),
    onboardingRecoveryService = inject(OnboardingRecoveryService),
    sentryService = inject(SentryService)
  ) =>
    actions$.pipe(
      ofType(authOnboardingRecoveryActions.onboardingFailedFromLocation),
      exhaustMap(({ companyProfiles }) =>
        recoverEach(
          companyProfiles,
          (companyProfile) => onboardingRecoveryService.recoverFromLocationFlow(companyProfile),
          reportRecoveryFailure(sentryService, 'location')
        ).pipe(
          // A workspace without a location is degraded, not unusable, so it never forces a logout.
          filter(Boolean),
          map(() => authOnboardingRecoveryActions.onboardingRecoverySuccess({ step: 'location' }))
        )
      )
    ),
  { functional: true }
);

// A short page would hide wallets and make a present one read as missing, so it recovers nothing.
const getAccountIdsWithoutWallet = (
  accountIds: UUID[],
  wallets: WalletDto[],
  totalCount: number
): UUID[] => {
  const accountIdsWithWallet = new Set(
    wallets.flatMap(({ walletAccesses }) => walletAccesses.map(({ accountId }) => accountId))
  );

  return wallets.length < totalCount
    ? []
    : accountIds.filter((accountId) => !accountIdsWithWallet.has(accountId));
};

// Reads redundant, and is: company.mapper.ts fills both fields from one array, so they never
// disagree on real data. Collapsing it to one field would rest on that mapper never changing.
const hasNoLocation = ({ _embedded, locations }: CompanyProfile): boolean =>
  _embedded?.locations?.length === 0 || locations?.length === 0;

// recoverFromCompanyFlow creates per legal entity, so two accounts sharing one must create once.
const getAccountsToRecover = (
  accounts: Account[],
  accountIds: Account['id'][]
): (Account & { legalEntity: LegalEntity })[] => [
  ...new Map(
    accounts
      .filter(
        (account: Account): account is Account & { legalEntity: LegalEntity } =>
          accountIds.includes(account.id) && !!account.legalEntity
      )
      .map((account) => [account.legalEntity.id, account] as const)
  ).values(),
];

// Nothing surfaces a per-workspace failure to the user, so Sentry is the only trace it leaves.
const reportRecoveryFailure =
  (sentryService: SentryService, step: OnboardingRecoveryStep) => (error: unknown) =>
    sentryService.captureException(`onboarding recovery failed for one workspace: ${step}`, error);

// Best effort, so one workspace failing neither cancels nor fails the others.
const recoverEach = <T>(
  items: T[],
  recover: (item: T) => Observable<unknown>,
  reportFailure: (error: unknown) => void
): Observable<boolean> =>
  forkJoin(
    items.map((item) =>
      recover(item).pipe(
        map(() => true),
        catchError((error: unknown) => {
          reportFailure(error);
          return of(false);
        })
      )
    )
  ).pipe(map((outcomes) => outcomes.some(Boolean)));

const toRecoveryAction = (step: OnboardingRecoveryStep) => (anyRecovered: boolean) =>
  anyRecovered
    ? authOnboardingRecoveryActions.onboardingRecoverySuccess({ step })
    : authOnboardingRecoveryActions.onboardingRecoveryFailed();
